import { api } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
} from "react";

interface CollegeLink {
  id: string;
  collegeSlug: string;
  syncStatus: string;
  lastSyncAt: string | null;
}

interface SyncContextValue {
  /** True while a sync is being processed on the server. */
  isSyncing: boolean;
  /** ISO timestamp of the last successful sync, or null. */
  lastSyncAt: string | null;
  /** Whether the user has at least one linked college. */
  hasLink: boolean;
  /** ID of the user's first college link (for on-demand refresh), or null. */
  linkId: string | null;
}

const SyncContext = createContext<SyncContextValue | null>(null);

/**
 * Provides sync state to the entire authenticated app.
 *
 * Mount this ONCE in the authenticated layout. It:
 * - Polls college-links every 3s while a sync is actively running
 * - Invalidates all data queries when sync completes
 */
export function SyncProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // --- College links query (with conditional polling) ---
  const { data: links } = useQuery({
    queryKey: queryKeys.collegeLinks.all,
    queryFn: () => api.get<CollegeLink[]>("/college-links"),
    select: (data) => data.data,
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      const isActive = data?.some(
        (l: CollegeLink) =>
          l.syncStatus === "pending" || l.syncStatus === "syncing",
      );
      return isActive ? 3000 : false;
    },
  });

  const firstLink = links?.[0];

  // Only "syncing" means worker is actively processing.
  // "pending" is DB default while initial background sync waits.
  const isWorkerSyncing = firstLink?.syncStatus === "syncing";

  const wasSyncing = useRef(false);
  useEffect(() => {
    if (isWorkerSyncing) {
      wasSyncing.current = true;
    } else if (wasSyncing.current && firstLink) {
      wasSyncing.current = false;
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.timetable.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.marks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.internalMarks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      });
    }
  }, [isWorkerSyncing, firstLink, queryClient]);

  const value: SyncContextValue = {
    isSyncing: isWorkerSyncing,
    lastSyncAt: firstLink?.lastSyncAt ?? null,
    hasLink: !!firstLink,
    linkId: firstLink?.id ?? null,
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

/**
 * Access sync state from any component inside the authenticated layout.
 * Must be used within a `<SyncProvider>`.
 */
export function useSync(): SyncContextValue {
  const ctx = useContext(SyncContext);
  if (!ctx) {
    throw new Error("useSync must be used within a <SyncProvider>");
  }
  return ctx;
}
